import {
  CornerRadii,
  Elevation,
  FontSize,
  FontWeight,
  LetterSpacing,
  LineHeight,
  Space,
  SystemColors
} from "./";

type Range<
  Start extends number,
  End extends number,
  Arr extends unknown[] = [],
  Acc extends number = never
> = Arr["length"] extends End
  ? Acc | Start | End
  : Range<
      Start,
      End,
      [...Arr, 1],
      Arr[Start] extends undefined ? Acc : Acc | Arr["length"]
    >;

type Spaces = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];

export type SpaceRange = Range<0, 192>;

export interface Theme {
  breakpoints: [string, string, string, string];
  fonts: {
    body: string;
    code: string;
  };
  colors: typeof SystemColors & ThemeColorTokens;
  cornerRadii: typeof CornerRadii;
  elevation: typeof Elevation;
  fontSizes: typeof FontSize;
  fontWeight: typeof FontWeight;
  letterSpacing: typeof LetterSpacing;
  lineHeight: typeof LineHeight;
  grid: {
    gridDesktopColumnCount: number;
    gridMobileColumnCount: number;
    gridTabletColumnCount: number;
  };
  lineHeights: typeof LineHeight;
  space: Spaces & typeof Space;
  transitions: {
    "transition-color-medium": string;
    "transition-color-slow": string;
    "transition-ease-out-fast": string;
    "transition-ease-out-medium": string;
    "transition-ease-out-slow": string;
  };
  zIndices: {
    dropdown: number;
    modal: number;
    navbar: number;
    panel: number;
  };
  sizes: {
    containerDesktopMargin: number;
    containerDesktopMaxWidth: number;
    containerMobileMargin: number;
    containerMobileMaxWidth: number;
    containerTabletMargin: number;
    containerTabletMaxWidth: number;
    gridDesktopColumnGutter: number;
    gridMobileColumnGutter: number;
    gridTabletColumnGutter: number;
  };
}

export interface ThemeColorTokens {
  // Brand Colors:
  "color-current-brand-50": string;
  "color-current-brand-100": string;
  "color-current-brand-200": string;
  "color-current-brand-300": string;
  "color-current-brand-400": string;
  "color-current-brand-500": string;
  "color-current-brand-600": string;
  "color-current-brand-700": string;
  "color-current-brand-800": string;
  "color-current-brand-900": string;
  "color-current-brand-950": string;
  // General Component Tokens: Color tokens used for broad functionality and capabilities across many components
  focus: string;
  "control-enabled": string;
  "control-hover": string;
  "control-click": string;
  "control-disabled": string;
  scrim: string;
  "surface-neutral-default": string;
  "surface-neutral-secondary": string;
  "surface-neutral-tertiary": string;
  "stroke-neutral-default": string;
  "stroke-neutral-secondary": string;
  "stroke-neutral-tertiary": string;
  "stroke-neutral-emphasis": string;
  "text-default": string;
  "text-secondary": string;
  "text-input-default": string;
  "text-input-disabled": string;
  "text-emphasis": string;
  "stroke-brand": string;
  "stroke-brand-secondary": string;
  "stroke-brand-tertiary": string;
  "stroke-brand-emphasis": string;
  "text-incidental": string;
  "text-disabled": string;
  // link
  "text-link": string;
  "text-link-hover": string;
  "text-link-click": string;
  // surface brand
  "surface-brand": string;
  "surface-brand-secondary": string;
  "surface-brand-teriary": string;
  "onSurface-brand-primary": string;
  "onSurface-brand-primary-hover": string;
  "onSurface-brand-primary-click": string;

  // Component Colors: Contextual use component tokens to represent colors as used between states and interactivity.
  // badge
  "badge-surface-color-neutral": string;
  "badge-surface-color-critical": string;
  "badge-surface-color-warning": string;
  "badge-surface-color-success": string;
  "badge-surface-color-info": string;
  // button
  "button-surface-filled-enabled": string;
  "button-surface-filled-hover": string;
  "button-surface-filled-click": string;
  "button-surface-filled-disabled": string;
  "button-onSurface-filled": string;
  "button-onSurface-filled-disabled": string;
  "button-onSurface-outline-enabled": string;
  "button-onSurface-outline-hover": string;
  "button-onSurface-outline-click": string;
  "button-onSurface-outline-disabled": string;
  "button-surface-outline": string;
  "button-surface-outline-disabled": string;
  "button-onSurface-subtle-enabled": string;
  "button-onSurface-subtle-hover": string;
  "button-onSurface-subtle-click": string;
  "button-onSurface-subtle-disabled": string;
  "button-surface-critical-enabled": string;
  "button-surface-critical-hover": string;
  "button-surface-critical-click": string;
  "button-surface-critical-disabled": string;
  "button-onSurface-critical": string;
  "button-onSurface-critical-disabled": string;
  // input
  "input-onSurface": string;
  "input-text": string;
  "input-stroke-enabled": string;
  "input-surface-enabled": string;
  "input-stroke-hover": string;
  "input-surface-hover": string;
  "input-stroke-click": string;
  "input-surface-click": string;
  "input-text-disabled": string;
  "input-surface-disabled": string;
  "input-stroke-disabled": string;
  "input-surface-error": string;
  "input-stroke-error": string;
  // feedback surface
  "feedback-surface-info": string;
  "feedback-surface-success": string;
  "feedback-surface-warning": string;
  "feedback-surface-critical": string;
  "feedback-surface-disabled": string;
  // feedback incidental
  "feedback-onSurface-incidental-info": string;
  "feedback-onSurface-incidental-success": string;
  "feedback-onSurface-incidental-warning": string;
  "feedback-onSurface-incidental-critical": string;
  // feedback on surface
  "feedback-onSurface-critical": string;
  "feedback-onSurface-critical-hover": string;
  "feedback-onSurface-critical-click": string;
  "feedback-onSurface-warning": string;
  "feedback-onSurface-warning-hover": string;
  "feedback-onSurface-warning-click": string;
  "feedback-onSurface-success": string;
  "feedback-onSurface-success-hover": string;
  "feedback-onSurface-success-click": string;
  "feedback-onSurface-info": string;
  "feedback-onSurface-info-hover": string;
  "feedback-onSurface-info-click": string;
  // slider component
  "slider-onsurface-notselected": string;
  "slider-onsurface-selected": string;
  "slider-surface-disabled": string;
  "slider-surface-not-selected": string;
  "slider-surface-selected-click": string;
  "slider-surface-selected-enabled": string;
  "slider-surface-selected-hover": string;
  // switch component
  "switch-onsurface-color": string;
  "switch-surface-selected-enabled": string;
  "switch-surface-selected-hover": string;
  "switch-surface-selected-click": string;
  "switch-surface-notselected-enabled": string;
  "switch-surface-notselected-hover": string;
  "switch-surface-notselected-click": string;
  "switch-onsurface-color-disabled": string;
  "switch-surface-color-disabled": string;
  // tab component
  "tab-text-default": string;
  "tab-text-selected": string;
  "tab-stroke-selected": string;
  "tab-stroke-hover": string;
  "tab-stroke-click": string;
  "tab-text-disabled": string;
}

export type ThemeProvided = Omit<
  Theme,
  "colorsDark" | "colorsLight" | "sizes"
> & {
  sizes: {
    [x in SpaceRange]: number;
  } & Theme["sizes"];
  fonts: ThemeFonts;
  colors: ThemeColors;
  borders: ThemeBorders;
  fontSizes: ThemeFontSizes;
  fontWeights: ThemeFontWeights;
};

export type ThemeMode = "light" | "dark";

type ThemeGeneric = "inherit" | "initial" | "revert" | "unset";

type ThemeFunction = (
  theme: ThemeProvided
) => string | number | null | undefined;

type ThemeValueEntry<T> = T | ThemeFunction | ThemeGeneric | null;

export type ThemeValue<T> =
  | ThemeValueEntry<T>
  | [ThemeValueEntry<T>, ThemeValueEntry<T>]
  | [ThemeValueEntry<T>, ThemeValueEntry<T>, ThemeValueEntry<T>];

export type ThemeBorders = ThemeValue<"none" | string>;

export type ThemeColors = ThemeValue<
  "currentColor" | "transparent" | keyof Theme["colors"]
>;
export type ThemeSizes = ThemeValue<
  | SpaceRange
  | keyof Theme["sizes"]
  | keyof Theme["space"]
  | 0
  | "50%"
  | "100%"
  | "100vh"
  | "100dvh"
  | "100vw"
  | "auto"
  | "none"
  | "content-width"
  | "max-content"
  | "min-content"
  | "fit-content"
>;
export type ThemeFonts = ThemeValue<keyof Theme["fonts"]>;
export type ThemeFontSizes = ThemeValue<
  keyof Theme["fontSizes"] | keyof Theme["lineHeight"]
>;
export type ThemeFontWeights = ThemeValue<keyof Theme["fontWeight"]>;
export type ThemeLetterSpaces = ThemeValue<keyof Theme["letterSpacing"]>;
export type ThemeMargins = ThemeValue<
  | keyof Theme["space"]
  | -96
  | -95
  | -94
  | -93
  | -92
  | -91
  | -90
  | -89
  | -88
  | -87
  | -86
  | -85
  | -84
  | -83
  | -82
  | -81
  | -80
  | -79
  | -78
  | -77
  | -76
  | -75
  | -74
  | -73
  | -72
  | -71
  | -70
  | -69
  | -68
  | -67
  | -66
  | -65
  | -64
  | -63
  | -62
  | -61
  | -60
  | -59
  | -58
  | -57
  | -56
  | -55
  | -54
  | -53
  | -52
  | -51
  | -50
  | -49
  | -48
  | -47
  | -46
  | -45
  | -44
  | -43
  | -42
  | -41
  | -40
  | -39
  | -38
  | -37
  | -36
  | -35
  | -34
  | -33
  | -32
  | -31
  | -30
  | -29
  | -28
  | -27
  | -26
  | -25
  | -24
  | -23
  | -22
  | -21
  | -20
  | -19
  | -18
  | -17
  | -16
  | -15
  | -14
  | -13
  | -12
  | -11
  | -10
  | -9
  | -8
  | -7
  | -6
  | -5
  | -4
  | -3
  | -2
  | -1
  | Range<0, 192>
  | "auto"
  | "1px"
  | "2px"
  | "3px"
  | "-1px"
  | "-2px"
  | "-3px"
>;
export type ThemeSpaces = ThemeValue<
  | SpaceRange
  | "1px"
  | "2px"
  | "3px"
  | "auto"
  | "50%"
  | "100%"
  | keyof Theme["space"]
>;
export type ThemeRadii = ThemeValue<keyof Theme["cornerRadii"]>;
export type ThemeShadows = ThemeValue<"none" | keyof Theme["elevation"]>;
export type ThemeTransitions = ThemeValue<
  "string" | "none" | keyof Theme["transitions"]
>;
export type ThemeZIndices = ThemeValue<
  keyof Theme["zIndices"] | 0 | 1 | 2 | 3 | 4
>;
export type ThemeString = ThemeValue<string>;

export interface ThemeBrandColors {
  "color-brand-100": string;
  "color-brand-200": string;
  "color-brand-300": string;
  "color-brand-400": string;
  "color-brand-50": string;
  "color-brand-500": string;
  "color-brand-600": string;
  "color-brand-700": string;
  "color-brand-800": string;
  "color-brand-900": string;
  "color-brand-950": string;
}

export const setColorsForTheme = (
  brandColors: ThemeBrandColors
): ThemeColorTokens => {
  return {
    // Brand Tokens:
    "color-current-brand-50": brandColors[`color-brand-50`],
    "color-current-brand-100": brandColors[`color-brand-100`],
    "color-current-brand-200": brandColors[`color-brand-200`],
    "color-current-brand-300": brandColors[`color-brand-300`],
    "color-current-brand-400": brandColors[`color-brand-400`],
    "color-current-brand-500": brandColors[`color-brand-500`],
    "color-current-brand-600": brandColors[`color-brand-600`],
    "color-current-brand-700": brandColors[`color-brand-700`],
    "color-current-brand-800": brandColors[`color-brand-800`],
    "color-current-brand-900": brandColors[`color-brand-900`],
    "color-current-brand-950": brandColors[`color-brand-950`],

    // General Tokens:
    // Interactive
    focus: SystemColors["color-system-blue-500"],
    scrim: "#25262733",

    // Control Colors
    "control-enabled": SystemColors["color-system-gray-700"],
    "control-hover": SystemColors["color-system-gray-900"],
    "control-click": SystemColors["color-system-gray-950"],
    "control-disabled": SystemColors["color-system-gray-300"],

    // Surface Neutral
    "surface-neutral-default": SystemColors.white,
    "surface-neutral-secondary": SystemColors["color-system-gray-50"],
    "surface-neutral-tertiary": SystemColors["color-system-gray-100"],

    // Surface Brand
    "surface-brand": brandColors[`color-brand-50`],
    "surface-brand-secondary": brandColors[`color-brand-100`],
    "surface-brand-teriary": brandColors[`color-brand-200`],
    "onSurface-brand-primary": brandColors[`color-brand-700`],
    "onSurface-brand-primary-hover": brandColors[`color-brand-900`],
    "onSurface-brand-primary-click": brandColors[`color-brand-950`],

    // Stroke Neutral
    "stroke-neutral-default": SystemColors["color-system-gray-50"],
    "stroke-neutral-secondary": SystemColors["color-system-gray-100"],
    "stroke-neutral-tertiary": SystemColors["color-system-gray-200"],
    "stroke-neutral-emphasis": SystemColors["color-system-gray-300"],

    // Stroke brand
    "stroke-brand": brandColors[`color-brand-100`],
    "stroke-brand-secondary": brandColors[`color-brand-200`],
    "stroke-brand-tertiary": brandColors[`color-brand-300`],
    "stroke-brand-emphasis": brandColors[`color-brand-600`],

    // Text Colors
    "text-default": SystemColors["color-system-gray-950"],
    "text-secondary": SystemColors["color-system-gray-700"],
    "text-emphasis": brandColors[`color-brand-900`],
    "text-input-default": SystemColors["color-system-gray-900"],
    "text-input-disabled": SystemColors["color-system-gray-300"],
    "text-incidental": SystemColors["color-system-gray-500"],
    "text-disabled": SystemColors["color-system-gray-300"],

    // Link colors
    "text-link": brandColors[`color-brand-700`],
    "text-link-hover": brandColors[`color-brand-900`],
    "text-link-click": brandColors[`color-brand-950`],

    // Component Tokens:
    // Button
    "button-surface-filled-enabled": brandColors[`color-brand-700`],
    "button-surface-filled-hover": brandColors[`color-brand-900`],
    "button-surface-filled-click": brandColors[`color-brand-950`],
    "button-surface-filled-disabled": SystemColors["color-system-gray-300"],
    "button-onSurface-filled": SystemColors.white,
    "button-onSurface-filled-disabled": SystemColors["color-system-gray-50"],
    "button-onSurface-outline-enabled": brandColors[`color-brand-700`],
    "button-onSurface-outline-hover": brandColors[`color-brand-900`],
    "button-onSurface-outline-click": brandColors[`color-brand-950`],
    "button-onSurface-outline-disabled": SystemColors["color-system-gray-300"],
    "button-surface-outline": SystemColors.white,
    "button-surface-outline-disabled": SystemColors["color-system-gray-50"],
    "button-onSurface-subtle-enabled": brandColors[`color-brand-700`],
    "button-onSurface-subtle-hover": brandColors[`color-brand-900`],
    "button-onSurface-subtle-click": brandColors[`color-brand-950`],
    "button-onSurface-subtle-disabled": SystemColors["color-system-gray-300"],
    "button-surface-critical-enabled": SystemColors["color-system-red-700"],
    "button-surface-critical-hover": SystemColors["color-system-red-900"],
    "button-surface-critical-click": SystemColors["color-system-red-950"],
    "button-surface-critical-disabled": SystemColors["color-system-gray-300"],
    "button-onSurface-critical": SystemColors.white,
    "button-onSurface-critical-disabled": SystemColors["color-system-gray-50"],

    // badge
    "badge-surface-color-neutral": SystemColors["color-system-gray-600"],
    "badge-surface-color-critical": SystemColors["color-system-red-600"],
    "badge-surface-color-warning": SystemColors["color-system-yellow-600"],
    "badge-surface-color-success": SystemColors["color-system-green-600"],
    "badge-surface-color-info": SystemColors["color-system-blue-600"],

    // Input
    "input-onSurface": SystemColors.white,
    "input-text": SystemColors["color-system-gray-900"],
    "input-stroke-enabled": SystemColors["color-system-gray-300"],
    "input-surface-enabled": brandColors[`color-brand-700`],
    "input-stroke-hover": SystemColors["color-system-gray-500"],
    "input-surface-hover": brandColors[`color-brand-900`],
    "input-stroke-click": SystemColors["color-system-gray-600"],
    "input-surface-click": brandColors[`color-brand-950`],
    "input-text-disabled": SystemColors["color-system-gray-300"],
    "input-surface-disabled": SystemColors["color-system-gray-50"],
    "input-stroke-disabled": SystemColors["color-system-gray-200"],
    "input-surface-error": SystemColors["color-system-red-700"],
    "input-stroke-error": SystemColors["color-system-red-700"],

    // Feedback
    "feedback-surface-info": SystemColors["color-system-blue-50"],
    "feedback-surface-success": SystemColors["color-system-green-50"],
    "feedback-surface-warning": SystemColors["color-system-yellow-50"],
    "feedback-surface-critical": SystemColors["color-system-red-50"],
    "feedback-onSurface-incidental-info": SystemColors["color-system-blue-500"],
    "feedback-onSurface-incidental-success":
      SystemColors["color-system-green-500"],
    "feedback-onSurface-incidental-warning":
      SystemColors["color-system-yellow-500"],
    "feedback-onSurface-incidental-critical":
      SystemColors["color-system-red-500"],
    "feedback-surface-disabled": SystemColors["color-system-gray-50"],
    "feedback-onSurface-critical": SystemColors["color-system-red-700"],
    "feedback-onSurface-critical-hover": SystemColors["color-system-red-900"],
    "feedback-onSurface-critical-click": SystemColors["color-system-red-950"],
    "feedback-onSurface-warning": SystemColors["color-system-yellow-700"],
    "feedback-onSurface-warning-hover": SystemColors["color-system-yellow-900"],
    "feedback-onSurface-warning-click": SystemColors["color-system-yellow-950"],
    "feedback-onSurface-success": SystemColors["color-system-green-700"],
    "feedback-onSurface-success-hover": SystemColors["color-system-green-900"],
    "feedback-onSurface-success-click": SystemColors["color-system-green-950"],
    "feedback-onSurface-info": SystemColors["color-system-blue-700"],
    "feedback-onSurface-info-hover": SystemColors["color-system-blue-900"],
    "feedback-onSurface-info-click": SystemColors["color-system-blue-950"],

    // Slider
    "slider-onsurface-notselected": SystemColors["color-system-gray-600"],
    "slider-onsurface-selected": brandColors[`color-brand-50`],
    "slider-surface-disabled": SystemColors["color-system-gray-300"],
    "slider-surface-not-selected": SystemColors["color-system-gray-100"],
    "slider-surface-selected-click": brandColors[`color-brand-950`],
    "slider-surface-selected-enabled": brandColors[`color-brand-700`],
    "slider-surface-selected-hover": brandColors[`color-brand-900`],

    // Switch
    "switch-onsurface-color": SystemColors.white,
    "switch-surface-selected-enabled": brandColors[`color-brand-950`],
    "switch-surface-selected-hover": brandColors[`color-brand-900`],
    "switch-surface-selected-click": brandColors[`color-brand-950`],
    "switch-surface-notselected-enabled": SystemColors["color-system-gray-300"],
    "switch-surface-notselected-hover": SystemColors["color-system-gray-500"],
    "switch-surface-notselected-click": SystemColors["color-system-gray-600"],
    "switch-onsurface-color-disabled": SystemColors["color-system-gray-50"],
    "switch-surface-color-disabled": SystemColors["color-system-gray-200"],

    // Tab
    "tab-text-default": SystemColors["color-system-gray-700"],
    "tab-text-selected": brandColors[`color-brand-700`],
    "tab-stroke-selected": brandColors[`color-brand-700`],
    "tab-stroke-hover": SystemColors["color-system-gray-200"],
    "tab-stroke-click": SystemColors["color-system-gray-700"],
    "tab-text-disabled": SystemColors["color-system-gray-300"]
  };
};
